import { type RefObject, useEffect, useState } from "react"

export const ScrollDirection = {
  LEFT: "left",
  RIGHT: "right",
  TOP: "top",
  BOTTOM: "bottom",
} as const

export type ScrollDirectionType =
  (typeof ScrollDirection)[keyof typeof ScrollDirection]

export type ScrollOverflow = {
  left: boolean
  right: boolean
  top: boolean
  bottom: boolean
}

const SCROLL_EPSILON_PX = 1

export const getScrollOverflow = (el: HTMLElement): ScrollOverflow => {
  const {
    scrollTop,
    scrollHeight,
    clientHeight,
    scrollLeft,
    scrollWidth,
    clientWidth,
  } = el

  return {
    left: scrollLeft > SCROLL_EPSILON_PX,
    right: scrollLeft < scrollWidth - clientWidth - SCROLL_EPSILON_PX,
    top: scrollTop > SCROLL_EPSILON_PX,
    bottom: scrollTop < scrollHeight - clientHeight - SCROLL_EPSILON_PX,
  }
}

export function useScroll(scrollRef: RefObject<HTMLElement | null>) {
  const [overflow, setOverflow] = useState<ScrollOverflow>({
    left: false,
    right: false,
    top: false,
    bottom: false,
  })

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let rafId = 0

    const update = () => {
      setOverflow(getScrollOverflow(el))
    }

    const scheduleUpdate = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        update()
      })
    }

    update()
    el.addEventListener("scroll", scheduleUpdate, { passive: true })

    const resizeObserver = new ResizeObserver(scheduleUpdate)
    resizeObserver.observe(el)
    // Content size can change without the viewport box resizing (e.g. zoom).
    for (const child of Array.from(el.children)) {
      resizeObserver.observe(child)
    }

    return () => {
      el.removeEventListener("scroll", scheduleUpdate)
      resizeObserver.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [scrollRef])

  const scrollDirection: ScrollDirectionType[] = [
    ...(overflow.left ? [ScrollDirection.LEFT] : []),
    ...(overflow.right ? [ScrollDirection.RIGHT] : []),
    ...(overflow.top ? [ScrollDirection.TOP] : []),
    ...(overflow.bottom ? [ScrollDirection.BOTTOM] : []),
  ]

  return {
    horizontal: { left: overflow.left, right: overflow.right },
    vertical: { top: overflow.top, bottom: overflow.bottom },
    overflow,
    scrollDirection,
  }
}
